import { z } from 'zod';

export const KdsStationSchema = z.enum(['GRILL', 'DRINKS', 'FRYER', 'DESSERT', 'PREP']);
export type KdsStation = z.infer<typeof KdsStationSchema>;

export const CreateMenuItemSchema = z.object({
  name: z.string().min(1).max(200),
  nameId: z.string().min(1).max(200),
  description: z.string().max(500).optional(),
  price: z.number().positive(),
  categoryId: z.string().uuid().optional(),
  outletId: z.string().uuid().optional(),
  station: KdsStationSchema.optional(),
  isAvailable: z.boolean().default(true),
  dietaryTags: z.array(z.string()).optional(),
  allergens: z.array(z.string()).optional(),
});
export type CreateMenuItemInput = z.infer<typeof CreateMenuItemSchema>;

export const UpdateMenuItemSchema = CreateMenuItemSchema.partial();
export type UpdateMenuItemInput = z.infer<typeof UpdateMenuItemSchema>;

export const CreateOutletSchema = z.object({
  name: z.string().min(1).max(200),
  location: z.string().max(500).optional(),
  isActive: z.boolean().default(true),
  operatingHours: z.string().max(100).optional(),
});
export type CreateOutletInput = z.infer<typeof CreateOutletSchema>;

export const UpdateOutletSchema = CreateOutletSchema.partial();
export type UpdateOutletInput = z.infer<typeof UpdateOutletSchema>;

// ============================================================================
// Kiosk-facing menu (GET /outlets/:id/menu) -- verified against
// fnb-service's OutletsService.findMenu() + Prisma schema. Money fields use
// coerce.number() since Prisma Decimal serializes to a string over the wire.
// ============================================================================

export const KioskMenuItemVariantSchema = z.object({
  id: z.string(),
  name: z.string(),
  priceAdjustment: z.coerce.number(),
});

export const KioskMenuItemModifierSchema = z.object({
  id: z.string(),
  name: z.string(),
  priceAdjustment: z.coerce.number(),
});

export const KioskMenuItemSchema = z.object({
  id: z.string(),
  categoryId: z.string(),
  outletId: z.string(),
  code: z.string(),
  name: z.string(),
  nameId: z.string(),
  description: z.string().nullable(),
  price: z.coerce.number(),
  imageUrl: z.string().nullable(),
  calories: z.number().nullable(),
  allergens: z.array(z.string()),
  dietaryTags: z.array(z.string()),
  preparationMinutes: z.number(),
  station: KdsStationSchema,
  isAvailable: z.boolean(),
  variants: z.array(KioskMenuItemVariantSchema),
  modifiers: z.array(KioskMenuItemModifierSchema),
});

export const KioskMenuCategorySchema = z.object({
  id: z.string(),
  outletId: z.string(),
  name: z.string(),
  nameId: z.string(),
  items: z.array(KioskMenuItemSchema),
});

/** findMenu() returns a bare array -- no {outletId,outletName} wrapper. */
export const KioskMenuResponseSchema = z.array(KioskMenuCategorySchema);

// ============================================================================
// Allergen check (POST /menu/check-allergens) -- verified against
// CheckAllergensDto + MenuService.checkAllergens().
// ============================================================================

export const CheckAllergensRequestSchema = z.object({
  itemIds: z.array(z.string()).min(1),
  customerAllergens: z.array(z.string()),
});

export const CheckAllergensResponseSchema = z.object({
  hasAllergenConflict: z.boolean(),
  conflictingItems: z.array(
    z.object({
      itemId: z.string(),
      itemName: z.string(),
      allergens: z.array(z.string()),
    }),
  ),
});

// ============================================================================
// Orders -- verified against CreateFnbOrderDto + OrdersService
// (createOrder/findByRef/findById/payWithWristband).
// ============================================================================

export const FnbPaymentMethodSchema = z.enum([
  'WRISTBAND_CREDIT',
  'CASH',
  'CARD',
  'QRIS',
  'EWALLET',
]);

export const PlaceFnbOrderRequestSchema = z.object({
  outletId: z.string(),
  customerId: z.string().optional(),
  wristbandId: z.string().optional(),
  paymentMethod: FnbPaymentMethodSchema,
  items: z.array(
    z.object({
      menuItemId: z.string(),
      variantId: z.string().optional(),
      quantity: z.number().int().min(1),
      modifiers: z
        .array(z.object({ name: z.string(), priceAdjustment: z.number().optional() }))
        .optional(),
      notes: z.string().max(256).optional(),
    }),
  ),
  notes: z.string().max(256).optional(),
});

/** Nested shape from createOrder/findByRef/findById -- menuItem/variant are sub-selects, not full rows. */
export const FnbOrderItemSchema = z.object({
  id: z.string(),
  orderId: z.string(),
  menuItemId: z.string(),
  variantId: z.string().nullable(),
  quantity: z.number().int(),
  unitPrice: z.coerce.number(),
  subtotal: z.coerce.number(),
  modifiers: z.array(z.object({ name: z.string(), priceAdjustment: z.number().optional() })).nullable(),
  notes: z.string().nullable(),
  station: KdsStationSchema,
  status: z.string(),
  menuItem: z.object({
    id: z.string(),
    name: z.string(),
    nameId: z.string(),
    imageUrl: z.string().nullable(),
  }),
  variant: KioskMenuItemVariantSchema.nullable(),
});

/**
 * Order shape returned by createOrder (via findByRef) and findById.
 * outlet/estimatedWaitMinutes are only present from findById (GET status by
 * internal ID) -- absent right after placing an order.
 */
export const FnbOrderSchema = z.object({
  id: z.string(),
  orderRef: z.string(),
  customerId: z.string().nullable(),
  wristbandId: z.string().nullable(),
  outletId: z.string(),
  status: z.string(),
  paymentMethod: z.string(),
  totalAmount: z.coerce.number(),
  notes: z.string().nullable(),
  estimatedReadyAt: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  items: z.array(FnbOrderItemSchema),
  outlet: z.object({ id: z.string(), name: z.string(), location: z.string().nullable() }).optional(),
  estimatedWaitMinutes: z.number().nullable().optional(),
});

/** payWithWristband returns a receipt shape, not a full FnbOrder -- no id/status/estimatedReadyAt. */
export const FnbWristbandPaymentReceiptSchema = z.object({
  orderRef: z.string(),
  items: z.array(FnbOrderItemSchema),
  totalAmount: z.coerce.number(),
  paymentMethod: z.literal('WRISTBAND_CREDIT'),
  remainingBalance: z.coerce.number(),
});
